import { ApolloError, QueryTuple } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';

import { getDelay, sleep } from '../getDelay';

export interface LazyQueryWithRetryOptions<TData, TVariables> {
  /** The tuple returned by a generated `use…LazyQuery` hook. */
  lazyQuery: QueryTuple<TData, TVariables>;
  /** Variables handed to `refetch` on every retry. */
  variables: TVariables;
  /**
   * `true` when `data` is terminal and polling must stop. Read on every
   * response, including the one `refetch` resolves with, because the tuple's
   * `data` does not update synchronously after a refetch.
   */
  isResolved: (data: TData) => boolean;
  /** Delay before the FIRST request. `0` fires it on mount. */
  initialDelay: number;
  /** Delay before the first retry; later retries back off via `getDelay`. */
  baseDelay: number;
  /** Upper bound `getDelay` grows towards. */
  maxDelay: number;
  /** Total number of requests, the first call included. */
  maxRetries: number;
}

export interface LazyQueryWithRetryResult<TData> {
  data: TData | undefined;
  error: ApolloError | undefined;
  loading: boolean;
  called: boolean;
  /** Every request was spent and `isResolved` never returned `true`. */
  reachMaxRetries: boolean;
}

/**
 * Fire a lazy query and keep refetching with exponential backoff until the
 * caller's `isResolved` accepts a response or the request budget is spent.
 *
 * Lifted (NCP-5461) from TnCSignedFlow's `getPaymentStatusQueryWithRetry` so the
 * Complete page can wait on the same cadence for the DocuSign status query: the
 * loop is the same, only the predicate and the delays differ. TnCSignedFlow
 * keeps its inline copy for now so its released timing is untouched.
 *
 * The attempt counter lives in a ref so a re-render caused by a response does
 * not restart the budget; the predicate and variables are read through refs so
 * callers may pass fresh closures without re-triggering the effect.
 */
export const useLazyQueryWithRetry = <TData, TVariables>({
  lazyQuery,
  variables,
  isResolved,
  initialDelay,
  baseDelay,
  maxDelay,
  maxRetries
}: LazyQueryWithRetryOptions<
  TData,
  TVariables
>): LazyQueryWithRetryResult<TData> => {
  const attempt = useRef(0);
  const [reachMaxRetries, setReachMaxRetries] = useState(false);

  const isResolvedRef = useRef(isResolved);
  isResolvedRef.current = isResolved;
  const variablesRef = useRef(variables);
  variablesRef.current = variables;

  const [load, { data, error, loading, called, refetch }] = lazyQuery;

  useEffect(() => {
    // `!= null`: Apollo hands back `null` data on some error paths, and the
    // predicate must only ever see a real response (the original loop's
    // `data && allResolved(...)` guard).
    const resolved = (candidate: TData | null | undefined): boolean =>
      candidate != null && isResolvedRef.current(candidate);

    const queryWithRetry = async (delay: number): Promise<void> => {
      if (loading || resolved(data)) {
        return;
      } else if (attempt.current >= maxRetries) {
        setReachMaxRetries(true);
        return;
      }
      await sleep(delay);
      if (!called) {
        load(); // first request
        attempt.current++;
      } else if (refetch) {
        const { data: refetchData } = await refetch(variablesRef.current);
        attempt.current++;
        if (resolved(refetchData)) {
          return;
        }
        await queryWithRetry(getDelay(delay, maxDelay));
      }
    };

    queryWithRetry(called ? baseDelay : initialDelay);
  }, [
    data,
    error,
    loading,
    refetch,
    called,
    load,
    initialDelay,
    baseDelay,
    maxDelay,
    maxRetries
  ]);

  return { data, error, loading, called, reachMaxRetries };
};
